import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Tooltip,
  IconButton,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ChatMessage } from "../types";
import { format, isToday, isYesterday } from "date-fns";

interface MessageBubbleProps {
  message: ChatMessage;
  contactName: string;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  contactName,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isInbound = message.direction === "inbound";
  const [copied, setCopied] = useState(false);

  const formatMessageTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return format(date, "HH:mm");
    } catch {
      return "";
    }
  };

  const handleCopy = () => {
    const text = message.message || message.template_name;
    if (text) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const renderStatus = () => {
    if (isInbound) return null;

    switch (message.status) {
      case "pending":
        return (
          <Tooltip title="Sending...">
            <AccessTimeIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }} />
          </Tooltip>
        );
      case "sent":
        return (
          <Tooltip title="Sent to WhatsApp">
            <CheckIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }} />
          </Tooltip>
        );
      case "delivered":
        return (
          <Tooltip title="Delivered to recipient phone">
            <DoneAllIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.7)" }} />
          </Tooltip>
        );
      case "read":
        return (
          <Tooltip title="Read by recipient">
            <DoneAllIcon sx={{ fontSize: 15, color: "#53bdeb" }} />
          </Tooltip>
        );
      case "failed":
        return (
          <Tooltip title={message.error || "Delivery failed"}>
            <ErrorOutlineIcon sx={{ fontSize: 15, color: "#ef4444" }} />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isInbound ? "flex-start" : "flex-end",
        width: "100%",
        my: 0.8,
        px: 1,
      }}
    >
      <Box
        sx={{
          position: "relative",
          maxWidth: { xs: "85%", sm: "68%", md: "54%" },
          minWidth: 140,
          borderRadius: isInbound
            ? "16px 16px 16px 4px"
            : "16px 16px 4px 16px",
          p: 1.6,
          background: isInbound
            ? isDark
              ? "#1E293B"
              : "#FFFFFF"
            : isDark
            ? "#143825"
            : "#DCF8C6",
          border: isInbound
            ? isDark
              ? "1px solid rgba(255, 255, 255, 0.08)"
              : "1px solid #E2E8F0"
            : isDark
            ? "1px solid rgba(34, 197, 94, 0.3)"
            : "1px solid #BAE6BD",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          color: isDark ? "#F8FAFC" : "#0F172A",
          transition: "all 0.18s ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            "& .copy-btn": { opacity: 1 },
          },
        }}
      >
        {/* Header row: Sender name or template tag */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: "0.74rem",
              color: isInbound
                ? isDark
                  ? "#E5BFA5"
                  : "#724E41"
                : isDark
                ? "#4ade80"
                : "#166534",
            }}
          >
            {isInbound ? contactName || "Customer" : message.created_by_name || "MMS Staff"}
          </Typography>

          {message.message_type === "template" && (
            <Chip
              icon={<AutoAwesomeIcon sx={{ fontSize: "11px !important", color: "#724E41 !important" }} />}
              label={message.template_name}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.68rem",
                fontWeight: 600,
                backgroundColor: isDark ? "rgba(114, 78, 65, 0.3)" : "#F7E6D8",
                color: isDark ? "#E5BFA5" : "#724E41",
                border: "1px solid",
                borderColor: isDark ? "#724E41" : "#E5BFA5",
              }}
            />
          )}

          {/* Hover copy button */}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            className="copy-btn"
            size="small"
            onClick={handleCopy}
            sx={{
              opacity: 0,
              p: 0.3,
              transition: "opacity 0.2s",
              color: isDark ? "#94A3B8" : "#94A3B8",
              "&:hover": { color: "#724E41" },
            }}
          >
            {copied ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#16a34a" }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 13 }} />
            )}
          </IconButton>
        </Stack>

        {/* Media or image card if any */}
        {message.media_url && (
          <Box
            component="img"
            src={message.media_url}
            alt="WhatsApp Media"
            sx={{
              maxWidth: "100%",
              maxHeight: 220,
              borderRadius: "10px",
              mb: 1,
              objectFit: "cover",
            }}
          />
        )}

        {/* Message body */}
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.5,
            fontSize: "0.92rem",
            color: isDark ? "#F8FAFC" : "#0F172A",
          }}
        >
          {message.message || (message.message_type === "template" ? message.template_name : "")}
        </Typography>

        {/* Template parameters breakdown if present */}
        {message.message_type === "template" && Boolean(message.template_variables?.trim()) && (
          <Box
            sx={{
              mt: 1,
              p: 1.2,
              borderRadius: "8px",
              backgroundColor: isDark ? "rgba(0, 0, 0, 0.25)" : "rgba(0, 0, 0, 0.03)",
              border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #E2E8F0",
              fontSize: "0.75rem",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: isDark ? "#94A3B8" : "#64748B", display: "block", mb: 0.3, fontWeight: 600 }}
            >
              Template variables:
            </Typography>
            {message.template_variables.split("\n").map((v, i) => (
              <Typography key={i} variant="caption" sx={{ display: "block", pl: 1, color: isDark ? "#E2E8F0" : "#334155" }}>
                • {`{{${i + 1}}}`}: <strong>{v}</strong>
              </Typography>
            ))}
          </Box>
        )}

        {/* Error message if failed */}
        {message.status === "failed" && message.error && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              color: "#dc2626",
              fontSize: "0.72rem",
              fontWeight: 500,
            }}
          >
            ⚠️ {message.error}
          </Typography>
        )}

        {/* Footer: Time + Status ticks */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={0.5}
          sx={{ mt: 0.6 }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.7rem",
              color: isDark ? "#94A3B8" : "#64748B",
            }}
          >
            {formatMessageTime(message.created_at)}
          </Typography>
          {renderStatus()}
        </Stack>
      </Box>
    </Box>
  );
};
